import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  Upload, Send, FileText, Loader2, Plus, Trash2, Sparkles, Folder as FolderIcon,
  FileSignature, User, Check,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { useWorkspace } from '../../context/WorkspaceContext';
import { apiFetch } from '../../fetchInterceptor';
import { FIELD_TYPES, fileToBase64 } from './helpers';
import { FieldPlacementEditor } from './FieldPlacementEditor';
import { PdfDropZone } from './PdfDropZone';
import { ContactPicker } from './ContactPicker';
import { FolderBrowserPicker } from './FolderBrowserPicker';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Wizard for creating a new signature request — upload PDF (or pick a
 * template), place fields, set the recipient, choose where to file the
 * signed copy, then send.
 *
 * Owns its own state (recipient, message, fields, etc.) and is invoked
 * inside the SignaturesHub Tabs page. Parent only passes static lookup
 * data (folders / templates / contacts) plus `onSent` callback for
 * refresh-after-success. Deep links via `initialContactId` and
 * `initialFolderId` carry over from row-action menus elsewhere in the app.
 *
 * Extracted from pages/SignaturesHub.jsx.
 */
export function NewRequestWizard({ folders, templates, contacts, onSent, initialContactId, initialFolderId }) {
  const { currentWorkspace } = useWorkspace();
  const [pdfDataUri, setPdfDataUri] = useState(null);
  const [pdfName, setPdfName] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [fields, setFields] = useState([]);
  const [contactId, setContactId] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientName, setRecipientName] = useState('');
  // Additional ordered signers beyond the primary recipient (multi-signer).
  const [extraSigners, setExtraSigners] = useState([]);
  const [subject, setSubject] = useState('Please sign this document');
  const [message, setMessage] = useState('');
  const [destFolderId, setDestFolderId] = useState('__root');
  const [newFolderName, setNewFolderName] = useState('');
  const [sending, setSending] = useState(false);
  // Tracks the folder name that the deep-link landed on, used both to render
  // the "Sending to: …" header chip and to compute template suggestions.
  const [deepLinkFolderName, setDeepLinkFolderName] = useState('');
  const [autoSelectedTemplateId, setAutoSelectedTemplateId] = useState('');
  // Capture the deep-link IDs in refs ONCE on first mount. The parent scrubs
  // the URL ~100ms after navigation, which blanks the `initialContactId` /
  // `initialFolderId` props before the contacts/folders fetches resolve.
  // Refs survive the parent re-renders so the consume-effect below has the
  // values it needs whenever the data eventually loads.
  const lockedContactIdRef = useRef(initialContactId || '');
  const lockedFolderIdRef = useRef(initialFolderId || '');

  // When a contact is selected, auto-fill the recipient + default the
  // destination folder to that contact's "Signed Documents" subfolder
  // (created automatically by the contact_default folder template on contact
  // creation). User can still override the folder picker afterwards.
  const handlePickContact = (id) => {
    setContactId(id);
    if (!id) return;
    const c = contacts.find((x) => x.contact_id === id);
    if (!c) return;
    setRecipientEmail(c.email || '');
    setRecipientName(c.name || '');
    // Find the contact's "Signed Documents" subfolder under its root.
    if (c.root_folder_id) {
      const signedFolder = folders.find(
        (f) => f.parent_folder_id === c.root_folder_id
          && f.name?.toLowerCase().includes('signed'),
      );
      setDestFolderId(signedFolder?.folder_id || c.root_folder_id);
    }
  };

  // Apply deep-link pre-fill once contacts & folders have loaded.
  // Reads from the refs (captured at mount) so it works even after the
  // parent scrubs the URL and the corresponding props blank.
  useEffect(() => {
    const lockedContactId = lockedContactIdRef.current;
    const lockedFolderId = lockedFolderIdRef.current;
    if (!lockedContactId && !lockedFolderId) return;
    if (!contacts.length && !folders.length) return;
    if (lockedContactId && !contactId) {
      handlePickContact(lockedContactId);
    }
    if (lockedFolderId) {
      const exists = folders.find((f) => f.folder_id === lockedFolderId);
      if (exists) {
        setDestFolderId(lockedFolderId);
        setDeepLinkFolderName(exists.name || '');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contacts.length, folders.length]);

  // Extract meaningful keywords from the deep-link folder name. Keeps the
  // signal small (single-word stems) so it overlaps generously with
  // human-named templates ("Engagement Letter 2026" / "MSA - Acme" / etc.).
  const folderKeywords = useMemo(() => {
    const name = (deepLinkFolderName || '').toLowerCase();
    if (!name) return [];
    const map = {
      contract: ['contract', 'agreement', 'msa'],
      agreement: ['agreement', 'contract', 'msa'],
      signed: ['contract', 'agreement', 'engagement', 'letter'],
      nda: ['nda', 'non-disclosure', 'confidential'],
      engagement: ['engagement', 'letter'],
      letter: ['letter', 'engagement'],
    };
    const hits = new Set();
    Object.entries(map).forEach(([trigger, kws]) => {
      if (name.includes(trigger)) kws.forEach((k) => hits.add(k));
    });
    return Array.from(hits);
  }, [deepLinkFolderName]);

  // Top templates whose name contains any of the folder keywords.
  // Capped at 3 to keep the suggestion strip compact.
  const suggestedTemplates = useMemo(() => {
    if (!folderKeywords.length || !templates.length) return [];
    return templates
      .filter((t) => {
        const n = (t.name || '').toLowerCase();
        return folderKeywords.some((k) => n.includes(k));
      })
      .slice(0, 3);
  }, [folderKeywords, templates]);

  // Auto-load when there's exactly one strong match AND the user hasn't
  // already chosen / uploaded something. Once-only per deep-link to avoid
  // clobbering a manual change.
  useEffect(() => {
    if (autoSelectedTemplateId) return;
    if (templateId || pdfDataUri) return;
    if (suggestedTemplates.length !== 1) return;
    const tpl = suggestedTemplates[0];
    setAutoSelectedTemplateId(tpl.template_id);
    handlePickTemplate(tpl.template_id);
    toast.success(`Auto-loaded template: ${tpl.name}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestedTemplates]);

  // Human-readable summary for the deep-link banner. Uses refs + state so it
  // survives the parent's URL-scrub (props blank ~100ms after mount).
  const deepLinkSummary = useMemo(() => {
    const lockedContactId = lockedContactIdRef.current;
    const lockedFolderId = lockedFolderIdRef.current;
    if (!lockedContactId && !lockedFolderId) return null;
    const contact = contacts.find((c) => c.contact_id === (contactId || lockedContactId));
    return {
      contactName: contact?.name || recipientName || null,
      folderName: deepLinkFolderName || null,
    };
  }, [contacts, contactId, recipientName, deepLinkFolderName]);

  const handleFile = async (file) => {
    if (!file) return;
    if (file.type !== 'application/pdf') {
      toast.error('Please upload a PDF file');
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      toast.error('PDF must be 20 MB or smaller');
      return;
    }
    const dataUri = await fileToBase64(file);
    setPdfDataUri(dataUri);
    setPdfName(file.name);
    setTemplateId('');
    setFields([]);
  };

  const handlePickTemplate = async (id) => {
    setTemplateId(id);
    if (!id) return;
    try {
      const res = await apiFetch(
        `${API_URL}/api/esign/templates/${id}?workspace_id=${currentWorkspace.workspace_id}`,
      );
      if (!res.ok) throw new Error('Failed to load template');
      const t = await res.json();
      setPdfDataUri(t.pdf_data_uri);
      setPdfName(t.name);
      setFields(t.fields || []);
    } catch (e) {
      toast.error(e.message);
    }
  };

  // Ordered signer list fed to the field editor (S1 = primary recipient).
  const signers = useMemo(
    () => [{ email: recipientEmail, name: recipientName }, ...extraSigners],
    [recipientEmail, recipientName, extraSigners],
  );

  const handleSend = async () => {
    if (!pdfDataUri) { toast.error('Upload a PDF or pick a template'); return; }
    if (!fields.length) { toast.error('Place at least one field'); return; }
    if (!recipientEmail) { toast.error('Enter the recipient email'); return; }
    const emailOk = (e) => /\S+@\S+\.\S+/.test(e || '');
    for (let i = 0; i < extraSigners.length; i += 1) {
      if (!emailOk(extraSigners[i].email)) {
        toast.error(`Signer ${i + 2} needs a valid email address`);
        return;
      }
    }
    if (signers.length > 1) {
      // Each signer needs at least one field, or they'd get an empty page.
      const counts = {};
      fields.forEach((f) => { const s = f.signer_index || 1; counts[s] = (counts[s] || 0) + 1; });
      const missing = signers.map((_, i) => i + 1).filter((i) => !counts[i]);
      if (missing.length) {
        toast.error(`No fields assigned to signer${missing.length > 1 ? 's' : ''} ${missing.join(', ')} — use the "Placing fields for" picker in step 2.`);
        return;
      }
    }

    setSending(true);
    try {
      const body = {
        template_id: templateId || null,
        pdf_base64: templateId ? null : pdfDataUri,
        fields,
        recipient_email: recipientEmail,
        recipient_name: recipientName || null,
        contact_id: contactId || null,
        recipients: signers.map((s, i) => ({
          email: s.email,
          name: s.name || null,
          contact_id: i === 0 ? (contactId || null) : null,
        })),
        subject,
        message: message || null,
        destination_folder_id: destFolderId === '__root' ? null : destFolderId,
        new_folder_name: newFolderName.trim() || null,
      };
      const res = await apiFetch(
        `${API_URL}/api/esign/requests?workspace_id=${currentWorkspace.workspace_id}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail?.message || data.detail || 'Failed to send');
      toast.success(signers.length > 1
        ? `Signature request sent — ${recipientEmail} signs first (${signers.length} signers total)`
        : `Signature request sent to ${recipientEmail}`);
      // Reset
      setPdfDataUri(null); setPdfName(''); setTemplateId(''); setFields([]);
      setContactId(''); setRecipientEmail(''); setRecipientName('');
      setExtraSigners([]);
      setMessage(''); setNewFolderName('');
      if (onSent) onSent();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Deep-link context banner — surfaced when the wizard was opened from a
          contact's Contracts folder. Makes it obvious the pre-fill landed so
          users don't wonder why the contact picker is already populated. */}
      {deepLinkSummary && (deepLinkSummary.contactName || deepLinkSummary.folderName) && (
        <div
          className="flex items-center gap-2 rounded-lg border border-emerald-200 dark:border-emerald-800/60 bg-emerald-50/60 dark:bg-emerald-950/30 px-3 py-2 text-xs"
          data-testid="esign-deeplink-banner"
        >
          <FileSignature className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
          <span className="text-emerald-900 dark:text-emerald-200">
            <span className="font-medium">Sending to:</span>{' '}
            {deepLinkSummary.contactName && (
              <span className="inline-flex items-center gap-1">
                <User className="w-3 h-3" />
                {deepLinkSummary.contactName}
              </span>
            )}
            {deepLinkSummary.contactName && deepLinkSummary.folderName && (
              <span className="mx-1.5 text-emerald-500">·</span>
            )}
            {deepLinkSummary.folderName && (
              <span className="inline-flex items-center gap-1">
                <FolderIcon className="w-3 h-3" />
                {deepLinkSummary.folderName}
              </span>
            )}
          </span>
        </div>
      )}

      {/* Step 1: PDF source */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">1. Source PDF</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Smart template suggestions — only shown when the deep-link folder
              has keywords AND templates whose names match. Lets the user
              1-tap-load the right template instead of paging through the
              full dropdown. Auto-selects when there's exactly one match. */}
          {suggestedTemplates.length > 0 && (
            <div className="rounded-lg border border-emerald-200 dark:border-emerald-800/60 bg-emerald-50/40 dark:bg-emerald-950/20 p-2.5 space-y-2" data-testid="esign-suggested-templates">
              <div className="flex items-center gap-1.5 text-[11px] font-medium text-emerald-900 dark:text-emerald-200 uppercase tracking-wider">
                <FileText className="w-3 h-3" />
                Suggested for this folder
              </div>
              <div className="flex flex-wrap gap-1.5">
                {suggestedTemplates.map((t) => {
                  const isActive = templateId === t.template_id;
                  return (
                    <button
                      key={t.template_id}
                      type="button"
                      onClick={() => handlePickTemplate(t.template_id)}
                      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] transition-colors ${
                        isActive
                          ? 'bg-emerald-600 border-emerald-600 text-white'
                          : 'bg-white dark:bg-background border-emerald-300 dark:border-emerald-800 text-emerald-900 dark:text-emerald-200 hover:bg-emerald-100 dark:hover:bg-emerald-950/40'
                      }`}
                      data-testid={`esign-suggested-template-${t.template_id}`}
                    >
                      {isActive && <Check className="w-3 h-3" />}
                      <span className="truncate max-w-[180px]">{t.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {templates.length > 0 && (
            <div className="space-y-1">
              <Label>Use a saved template</Label>
              <Select value={templateId || ''} onValueChange={handlePickTemplate}>
                <SelectTrigger data-testid="template-select">
                  <SelectValue placeholder="— None (upload new PDF below) —" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map(t => (
                    <SelectItem key={t.template_id} value={t.template_id}>
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {!templateId && (
            <div className="space-y-1">
              <Label>Or upload a fresh PDF</Label>
              <PdfDropZone
                onFile={handleFile}
                fileName={pdfName}
                testId="pdf-upload-input"
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Step 2: Place fields */}
      {pdfDataUri && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">2. Place fields on the PDF</CardTitle>
            <CardDescription>
              Click a tool above the preview, then click on the PDF where you want
              the recipient to sign / type. Use the page controls to jump to any
              page of a multi-page document.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <FieldPlacementEditor pdfDataUri={pdfDataUri} fields={fields} onChange={setFields} signers={signers} />
          </CardContent>
        </Card>
      )}

      {/* Step 3: Recipient + destination folder */}
      {pdfDataUri && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">3. Recipient &amp; destination</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Searchable contact picker. Selecting a contact auto-fills the
                email + name AND defaults the destination folder to that
                contact's "Signed Documents" subfolder (auto-created by the
                contact_default folder template). */}
            <div className="md:col-span-2 space-y-1">
              <Label>Pick a contact (recommended)</Label>
              <ContactPicker
                contacts={contacts}
                value={contactId}
                onChange={handlePickContact}
              />
              <p className="text-[11px] text-muted-foreground">
                Or fill recipient details manually below. Linking to a contact
                routes the signed copy to their folder automatically and shows the
                request on their profile.
              </p>
            </div>
            <div className="space-y-1">
              <Label>Recipient email *</Label>
              <Input
                type="email"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                placeholder="client@example.com"
                data-testid="recipient-email-input"
              />
            </div>
            <div className="space-y-1">
              <Label>Recipient name</Label>
              <Input
                value={recipientName}
                onChange={(e) => setRecipientName(e.target.value)}
                placeholder="Jane Smith"
                data-testid="recipient-name-input"
              />
            </div>
            {/* Multi-signer: ordered additional signers. Signer 2 only gets
                their email AFTER signer 1 completes, and so on. */}
            <div className="md:col-span-2 space-y-2" data-testid="extra-signers-block">
              {extraSigners.map((s, i) => (
                <div key={i} className="flex items-end gap-2">
                  <span className="text-[11px] font-mono text-muted-foreground pb-2.5 shrink-0 w-14">Signer {i + 2}</span>
                  <div className="flex-1 space-y-1">
                    <Input
                      type="email"
                      value={s.email}
                      onChange={(e) => setExtraSigners(extraSigners.map((x, j) => (j === i ? { ...x, email: e.target.value } : x)))}
                      placeholder={`signer${i + 2}@example.com`}
                      data-testid={`extra-signer-email-${i}`}
                    />
                  </div>
                  <div className="flex-1 space-y-1">
                    <Input
                      value={s.name}
                      onChange={(e) => setExtraSigners(extraSigners.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))}
                      placeholder="Name (optional)"
                      data-testid={`extra-signer-name-${i}`}
                    />
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="shrink-0 text-rose-600 hover:text-rose-700"
                    onClick={() => setExtraSigners(extraSigners.filter((_, j) => j !== i))}
                    aria-label={`Remove signer ${i + 2}`}
                    data-testid={`remove-extra-signer-${i}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="gap-1.5"
                  onClick={() => setExtraSigners([...extraSigners, { email: '', name: '' }])}
                  disabled={extraSigners.length >= 9}
                  data-testid="add-signer-btn"
                >
                  <Plus className="w-3.5 h-3.5" /> Add another signer
                </Button>
                {extraSigners.length > 0 && (
                  <span className="text-[11px] text-muted-foreground">
                    Signs in order — each signer is emailed after the previous one finishes. Assign fields per signer in step 2.
                  </span>
                )}
              </div>
            </div>
            <div className="space-y-1 md:col-span-2">
              <Label>Subject</Label>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                data-testid="subject-input"
              />
            </div>
            <div className="space-y-1 md:col-span-2">
              <Label>Message to recipient (optional)</Label>
              <Textarea
                rows={3}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Hi Jane, please review and sign at your convenience."
                data-testid="message-input"
              />
            </div>
            <div className="space-y-1 md:col-span-2">
              <Label>File signed copy into</Label>
              <FolderBrowserPicker
                folders={folders}
                value={destFolderId}
                onChange={setDestFolderId}
              />
            </div>
            <div className="space-y-1 md:col-span-2">
              <Label>Or create a new subfolder</Label>
              <Input
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                placeholder="e.g. 2026 Engagement"
                data-testid="new-folder-name-input"
              />
              <p className="text-[11px] text-muted-foreground">
                {newFolderName ? `Will be created under "${destFolderId === '__root' ? 'Documents Hub (root)' : (folders.find(f => f.folder_id === destFolderId)?.name || 'selected folder')}".` : 'Leave blank to use the folder above.'}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Send */}
      {pdfDataUri && (
        <div className="flex justify-end">
          <Button
            onClick={handleSend}
            disabled={sending}
            size="lg"
            className="gap-2"
            data-testid="send-signature-request-btn"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Send for signature
          </Button>
        </div>
      )}
    </div>
  );
}

// ── Requests tab ───────────────────────────────────────────────────────────


export default NewRequestWizard;
